'use client';

import { useQuery } from '@tanstack/react-query';
import { gatedAction } from '@/lib/api/auth-gate';
import { getOnboardingStatusAction } from './actions';
import { fetchOnboardingSampleEmail } from './client';
import type { OnboardingSampleEmail, OnboardingStatus } from './types';

/**
 * Fetches the user's onboarding status. Used by the Getting Started hub
 * (role-aware setup-firm completion) and the sidebar progress widget.
 *
 * Cached for 5 minutes — the status only changes when the user uploads
 * a letterhead / signature or saves an email signature, which already
 * invalidate the relevant React Query keys at their own action sites.
 */
export function useOnboardingStatus() {
  return useQuery<OnboardingStatus>({
    queryKey: ['onboarding', 'status'],
    queryFn: () => gatedAction(() => getOnboardingStatusAction()),
    staleTime: 5 * 60 * 1000,
    retry: false
  });
}

/**
 * The sample client email (VALE-1633). It carries the reader's own name, so a
 * profile write refetches it: the key sits under `['onboarding']`, which the
 * `profile` dependency bundle invalidates.
 */
export function useOnboardingSampleEmail() {
  return useQuery<OnboardingSampleEmail>({
    queryKey: ['onboarding', 'sample-email'],
    queryFn: fetchOnboardingSampleEmail
  });
}
